import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/security/service-1.png",
    title: "Code Scanning",
    description:
      "Integrate static application security testing (SAST) with tools like SonarQube and CodeQL to detect code vulnerabilities and quality issues during CI builds.",
  },
  {
    logo_images: "../assets/icons/security/service-2.png",
    title: "Dependency & Supply Chain Scanning",
    description:
      "Leverage Snyk, Dependabot, and OWASP tools to scan open-source dependencies, containers, and IaC templates for known CVEs and license risks.",
  },
  {
    logo_images: "../assets/icons/security/service-3.png",
    title: "Container & Image Security",
    description:
      "Automate container image scanning, policy enforcement, and admission controls across registries and Kubernetes clusters.",
  },
  {
    logo_images: "../assets/icons/security/service-4.png",
    title: "CI/CD Integration",
    description:
      "Embed security gates into Jenkins, GitHub Actions, GitLab, and Azure DevOps pipelines with policy-as-code, approvals, and automated remediation workflows.",
  },
  {
    logo_images: "../assets/icons/security/service-5.png",
    title: "Reporting & Governance",
    description:
      "Provide executive dashboards, developer-friendly findings, and ticket automation integrated with JIRA, ServiceNow, and SIEM solutions.",
  },
];

const benefits = [
  "Shift-left security with automated scans early in the SDLC",
  "Continuous visibility into vulnerabilities across code, dependencies, and containers",
  "Faster remediation with prioritized findings and developer workflows",
  "Audit-ready reports demonstrating compliance with industry standards",
  "Reduced risk of supply chain attacks and production incidents",
  "Culture of shared responsibility between Dev, Sec, and Ops teams",
];

const expertise = [
  "Certified security engineers experienced with Snyk, SonarQube, Aqua, and Trivy",
  "Security automation across microservices, serverless, and infrastructure-as-code",
  "Policy-as-code frameworks using OPA, Sentinel, and custom governance engines",
  "Integration with secrets management, IAM, and zero-trust architectures",
  "Remediation playbooks, training, and developer enablement programs",
  "Managed DevSecOps services with 24/7 monitoring and SLA-backed response",
];

const useCases = [
  {
    title: "Enterprise DevSecOps Transformation",
    description:
      "Embedded SonarQube and Snyk scans into 300+ pipelines with centralized governance, reducing critical vulnerabilities by 70% within six months.",
  },
  {
    title: "Container Security Platform",
    description:
      "Implemented container scanning and admission controls for Kubernetes clusters using Snyk and Gatekeeper, preventing unapproved images from production.",
  },
  {
    title: "Regulatory Compliance Automation",
    description:
      "Generated automated security reports and ticket workflows aligned to PCI and HIPAA requirements, streamlining audit readiness.",
  },
  {
    title: "Supply Chain Risk Management",
    description:
      "Established SBOM generation, dependency upgrades, and policy enforcement to mitigate third-party software risks across applications.",
  },
];

const reasons = [
  "Holistic DevSecOps approach integrating people, process, and tooling",
  "Vendor-neutral expertise across commercial and open-source scanners",
  "Security-first pipeline frameworks with measurable KPIs",
  "Rapid onboarding with reference architectures and automation blueprints",
  "Collaboration with security, compliance, and engineering stakeholders",
  "Ongoing managed services ensuring continuous protection and improvements",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const SecurityScanning = () => (
  <>
    <Helmet>
      <meta
        name="description"
        content="Don't let hackers find bugs before you do. Our Security Scanning Service detects hidden threats instantly. Fast, reliable, and comprehensive protection."
      />
      <meta name="copyright" content="codeglim" />
      <meta
        property="og:title"
        content="Professional Security Scanning Service | Protect Your Business | Ayanshtech Solutions"
      />
      <meta property="og:site_name" content="Ayanshtech Solutions" />
      <meta
        property="og:url"
        content="http://ayanshtechnology.com/devops-security-scanning"
      />
      <meta
        property="og:description"
        content="Don't let hackers find bugs before you do. Our Security Scanning Service detects hidden threats instantly. Fast, reliable, and comprehensive protection."
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content="http://ayanshtechnology.com/assets/logo.png"
      />

      <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Professional Security Scanning Service | Protect Your Business | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Don't let hackers find bugs before you do. Our Security Scanning Service detects hidden threats instantly. Fast, reliable, and comprehensive protection."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />
      <link
        rel="canonical"
        href="http://ayanshtechnology.com/devops-security-scanning"
      />
      <title>
        Professional Security Scanning Service | Protect Your Business | Ayanshtech Solutions
      </title>
    </Helmet>

    <div className="flex flex-col items-center justify-center mb-6 p-6">
      <section className="py-5 w-full">
        <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
          <div className="lg:w-3/4 lg:text-left p-4">
            <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Security Scanning (Snyk, SonarQube)
            </h1>
            <p className="sm:text-base md:text-lg lg:text-lg mb-3">
              Embed security into every commit with automated scanning across
              code, dependencies, containers, and infrastructure. We integrate
              Snyk, SonarQube, and complementary tools to deliver actionable
              insights and continuous protection.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
            >
              Strengthen DevSecOps
            </button>
          </div>
          <div className="flex  justify-center mb-12 lg:mb-0">
            <img
              src="../assets/images/devops-pages/scanning-banner.png"
              alt="Security Scanning"
              className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* <section className="mb-5 mt-10 p-4 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          About DevSecOps Scanning
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Modern DevOps demands security automation. By embedding scanners into
          CI/CD pipelines, teams detect vulnerabilities early, enforce policies,
          and maintain compliance without slowing delivery.
        </p>
      </section> */}

      <section className="text-center mb-5 mt-12 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Security Scanning Services
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Automation offerings covering SAST, SCA, container security, and
          pipeline integrations.
        </p>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              
            >
              <div className="flex items-center gap-2">
                <div className="w-[60px] h-[60px]  ">
                  <img src={service.logo_images} className="w-full" alt="" />
                </div>
                <h3 className="text-lg font-bold">{service.title}</h3>
              </div>
              <p className="text-lg text-gray-700">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-20 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Benefits of Security Scanning
        </h2>
      </section>
      <div className="p-4 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="p-6 sm:px-8 md:px-12 lg:px-16 w-full mt-10 ">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Security Expertise
          </h2>
        </div>
        <div className="flex flex-col md:flex-row justify-center item-center ">
        <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-6xl mx-auto space-y-3 text-gray-700">
          {expertise.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="w-full md:w-1/3">
          <img src="../assets/images/devops-pages/scanning-image.png" alt="" />
        </div>
        </div>
      </section>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Use Cases
        </h2>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {useCases.map((useCase) => (
            <div
              key={useCase.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
             
            >
              <h3 className="text-lg font-bold text-gray-900">
                {useCase.title}
              </h3>
              <p className="text-lg text-gray-700">{useCase.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Why Choose Ayanshtech
        </h2>
      </section>
      <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{reason}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={scrollToContact}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Automate Security Scans
          </button>
        </div>
      </div>

      <div id="contact-section" className="w-full mt-12">
        <ContactUs />
      </div>
    </div>
  </>
);

export default SecurityScanning;

